/**
 * Holatlar 12a: once, on the home page, the app asks before the browser does, because a browser's
 * own refusal cannot be asked again. "Keyinroq" leaves the switch in Profil, Bildirishnomalar.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type JSX } from "react";

import { queryKeys } from "@/api/queryKeys";
import { useTranslation } from "@/i18n";
import { Logo } from "@/ui/brand";
import { Button } from "@/ui/button";
import { Dialog } from "@/ui/dialog";
import { toast } from "@/ui/toast";

import { enablePush, pushPublicKey, pushState, rememberAsked, wasAsked } from "./push";

export function PushPrompt(): JSX.Element | null {
  const { t } = useTranslation();
  const [closed, setClosed] = useState(() => pushState() !== "default" || wasAsked());
  const key = useQuery({
    queryKey: queryKeys.pushPublicKey(),
    queryFn: pushPublicKey,
    enabled: !closed,
    staleTime: Infinity,
    retry: false,
  });
  const enable = useMutation({
    mutationFn: (publicKey: string) => enablePush(publicKey),
    onSettled: () => {
      rememberAsked();
      setClosed(true);
    },
    onError: () => {
      toast.error(t("hol.push.failed"));
    },
  });
  const publicKey = key.data ?? "";
  if (closed || publicKey === "") return null;
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          rememberAsked();
          setClosed(true);
        }
      }}
      title={t("hol.push.title")}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              rememberAsked();
              setClosed(true);
            }}
          >
            {t("hol.push.later")}
          </Button>
          <Button
            loading={enable.isPending}
            onClick={() => {
              enable.mutate(publicKey);
            }}
          >
            {t("hol.push.allow")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        <Logo kind="mark" size={36} />
        <p className="text-sm text-ink-muted">{t("hol.push.subtitle")}</p>
        <p className="text-sm text-ink">{t("hol.push.scope")}</p>
      </div>
    </Dialog>
  );
}
